import EntityModel from '@/common/models/entity-model';
import GatewayModel from '@/common/models/gateway-model';
import ContactModel from '@/common/models/contact-model';
import PaymentInstrumentModel from '@/common/models/payment-instrument-model';
import PaymentCardModel from '@/common/models/payment-card-model';
import BankAccountModel from '@/common/models/bank-account-model';
import CustomerModel from '@/common/models/customer-model';
import WebsiteModel from '@/common/models/website-model';
import GatewayAccountModel from '@/common/models/gateway-account-model';
import LeadSourceModel from '@/common/models/lead-source-model';
import moment from 'moment';
import {DateTimeFormats} from '@/common/datetime-formats';
import {dateUTC} from '@/common/format/date';

export default class TransactionModel extends EntityModel {
    static Types = {
        authorize: 'authorize',
        capture: 'capture',
        credit: 'credit',
        refund: 'refund',
        sale: 'sale',
        void: 'void',
    };

    static Statuses = {
        completed: 'completed',
        connectionError: 'connection-error',
        neverSent: 'never-sent',
        pending: 'pending',
        sending: 'sending',
        refunded: 'refunded',
        partiallyRefunded: 'partially-refunded',
        suspended: 'suspended',
        timeout: 'timeout',
        waitingCapture: 'waiting-capture',
        waitingRefund: 'waiting-refund',
        waitingApproval: 'waiting-approval',
        voided: 'voided',
    };

    static Results = {
        abandoned: 'abandoned',
        approved: 'approved',
        canceled: 'canceled',
        declined: 'declined',
        unknown: 'unknown',
    };

    hydrate({
        acquirerName = null,
        amount = null,
        billingAddress = null,
        billingContactId = null,
        bin = null,
        childTransactions = null,
        createdTime = null,
        currency = null,
        customerId = null,
        dcc = null,
        description = null,
        gateway = null,
        gatewayAccountId = null,
        gatewayName = null,
        gatewayResponse = null,
        gatewayTransactionId = null,
        hadDiscrepancy = null,
        has3ds = null,
        hasDcc = null,
        id = null,
        invoiceIds = null,
        isDisputed = null,
        isRebill = null,
        isReconciled = null,
        isRetry = null,
        method = null,
        parentTransactionId = null,
        paymentCardId = null,
        paymentInstrument,
        processedTime = null,
        processorAccount = null,
        processorResponse = null,
        retryInstruction = null,
        rebillNumber = null,
        redirectUrls = null,
        result = null,
        retriedTransactionId = null,
        retriesResult = null,
        retryNumber = null,
        riskMetadata = null,
        riskScore = null,
        scheduledTime = null,
        status = null,
        subscriptionIds = null,
        organizationId = null,
        type = null,
        updatedTime = null,
        velocity = null,
        websiteId = null,
        isProcessedOutside = null,
        ...otherFields
    } = {}) {
        this.acquirerName = acquirerName;
        this.amount = amount;
        this.billingAddress = new ContactModel(billingAddress || {});
        this.billingContactId = billingContactId;
        this.bin = bin;
        this.childTransactions = childTransactions;
        this.createdTime = createdTime;
        this.currency = currency;
        this.customerId = customerId;
        this.dcc = dcc;
        this.description = description;
        this.gatewayAccountId = gatewayAccountId;
        this.gatewayName = gatewayName;
        this.gateway = new GatewayModel(gateway || {});
        this.gatewayResponse = gatewayResponse;
        this.gatewayTransactionId = gatewayTransactionId;
        this.hadDiscrepancy = hadDiscrepancy;
        this.has3ds = has3ds;
        this.hasDcc = hasDcc;
        this.id = id;
        this.invoiceIds = invoiceIds;
        this.isDisputed = isDisputed;
        this.isRebill = isRebill;
        this.isReconciled = isReconciled;
        this.isRetry = isRetry;
        this.method = method;
        this.parentTransactionId = parentTransactionId;
        this.paymentCardId = paymentCardId;
        this.paymentInstrument = new PaymentInstrumentModel(paymentInstrument || {});
        this.processedTime = processedTime;
        this.processorAccount = processorAccount;
        this.processorResponse = processorResponse;
        this.retryInstruction = retryInstruction;
        this.rebillNumber = rebillNumber;
        this.redirectUrls = redirectUrls;
        this.result = result;
        this.retriedTransactionId = retriedTransactionId;
        this.retriesResult = retriesResult;
        this.retryNumber = retryNumber;
        this.riskMetadata = riskMetadata || {};
        this.riskScore = riskScore;
        this.scheduledTime = scheduledTime;
        this.status = status;
        this.subscriptionIds = subscriptionIds;
        this.type = type;
        this.updatedTime = updatedTime;
        this.velocity = velocity;
        this.websiteId = websiteId;
        this.organizationId = organizationId;
        this.isProcessedOutside = isProcessedOutside;
        // workaround for a property starting with a digit
        this.threeDSecure = otherFields['3ds'];

        // embedded entities
        this.bankAccount = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'bankAccount',
            model: BankAccountModel,
        });
        this.customer = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'customer',
            model: CustomerModel,
        });
        this.parentTransaction = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'parentTransaction',
            model: TransactionModel,
        });
        // First declined transaction that initiates retries.
        this.retriedTransaction = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'retriedTransaction',
            model: TransactionModel,
        });
        this.gatewayAccount = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'gatewayAccount',
            model: GatewayAccountModel,
        });
        this.paymentCard = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'paymentCard',
            model: PaymentCardModel,
        });
        this.website = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'website',
            model: WebsiteModel,
        });
        this.leadSource = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'leadSource',
            model: LeadSourceModel,
        });
        // Refund transactions related to this parent
        this.refundedTransactions = null;
        // Capture transactions related to this parent
        this.captureTransactions = null;
        // Amount from the transaction allocated to the currently loaded invoice
        this.invoiceAmount = null;
        // eslint-disable-next-line no-underscore-dangle
        this.refundLink = otherFields._links.find(link => link.rel === 'refundUrl');
        // Only if this transaction is the initial declined transaction
        this.lastRetry = null;
        // derived properties
        this.hasChildTransactions = this.childTransactions.length;
        this.statusMap = TransactionModel.getStatusMap(this.status);
        this.resultMap = TransactionModel.getResultMap(this.result);
        this.typeMap = TransactionModel.getTypeMap(this.type);
    }

    get canCancel() {
        return this.statusMap.isPending;
    }

    get canVoid() {
        return this.resultMap.isApproved && !this.hasChildTransactions
            && (this.typeMap.isAuthorize
                || (this.statusMap.isWaitingCapture
                    && (this.typeMap.isCapture || this.typeMap.isSale)
                )
            );
    }

    get isVoidType() {
        return this.type === TransactionModel.Types.void;
    }

    get isVoided() {
        return this.status === TransactionModel.Statuses.voided;
    }

    get canCapture() {
        return this.resultMap.isApproved && this.typeMap.isAuthorize && !this.hasChildTransactions;
    }

    get isCaptureType() {
        return this.type === TransactionModel.Types.capture;
    }

    get isCaptured() {
        return this.typeMap.isAuthorize && this.hasCaptureTransactions;
    }

    get hasCaptureTransactions() {
        return this.captureTransactions && this.captureTransactions.length;
    }

    get canRefund() {
        return !!this.refundLink && !this.isDisputed;
    }

    get isRefundType() {
        return this.type === TransactionModel.Types.refund;
    }

    get isRefunded() {
        return [
            TransactionModel.Statuses.refunded,
            TransactionModel.Statuses.partiallyRefunded,
        ].includes(this.status);
    }

    get isDeclined() {
        return this.result === TransactionModel.Results.declined;
    }

    get canDispute() {
        return (this.resultMap.isApproved && this.typeMap.isSale)
            || (this.resultMap.isApproved && this.typeMap.isCapture);
    }

    get isScheduled() {
        return this.statusMap.isPending
            && this.resultMap.isUnknown
            && moment.utc(this.scheduledTime).isAfter(moment.utc());
    }

    get hasRefunds() {
        return this.refundedTransactions && this.refundedTransactions.length;
    }

    get hasBeenRetried() {
        return this.retriesResult !== null;
    }

    /**
     * Calculate the refundable amount for the transaction, return value is cached.
     * Method of calculate was ported from previous code. as it was.
     * @return {number}
     */
    get refundableAmount() {
        if (this.$refundableAmount !== undefined || !this.canRefund) {
            // cached or cannot refund
            return this.$refundableAmount || 0;
        }
        if (this.hasRefunds) {
            const appliedRefunds = this.refundedTransactions.reduce(
                (sum, refund) => sum + parseFloat(refund.amount), 0,
            );
            this.$refundableAmount = Math.round((this.amount - appliedRefunds) * 100) / 100;
        } else {
            this.$refundableAmount = this.amount;
        }
        return this.$refundableAmount;
    }

    /**
     * The signed amount reflects how the transaction influences the customer. Transactions
     * resulting in a charge for the customer have a negative sign, while all others have a
     * positive sign.
     */
    get signedAmount() {
        if (this.invoiceAmount === 0) {
            return 0;
        }
        // return "amount" if "invoiceAmount" is "null"
        const amount = this.invoiceAmount || this.amount;

        if (this.isRefundType || this.isVoidType) {
            return amount;
        }

        return amount * -1;
    }

    /**
     * In certain scenarios a transaction may be associated with an invoice, but have a $0
     * contribution towards its payment. This method checks if this is the case.
     */
    get affectsCurrentInvoice() {
        if (this.invoiceAmount === 0 && this.amount > 0) {
            return false;
        }
        return true;
    }

    get isApproved() {
        return this.result === TransactionModel.Results.approved;
    }

    get isSaleType() {
        return this.type === TransactionModel.Types.sale;
    }

    get explanation() {
        const dateTime = this.processedTime ? this.processedTime : this.scheduledTime;
        const processedDate = dateUTC(dateTime, DateTimeFormats.shortDate);

        if (this.result === TransactionModel.Results.approved) {
            switch (this.type) {
                case TransactionModel.Types.sale:
                case TransactionModel.Types.capture:
                    return `Payment received ${processedDate}`;
                case TransactionModel.Types.authorize:
                    return `Payment authorized ${processedDate}`;
                case TransactionModel.Types.void:
                    return `Transaction voided ${processedDate}`;
                case TransactionModel.Types.refund:
                    return `Transaction refunded ${processedDate}`;
                default:
                    return `Transaction credited ${processedDate}`;
            }
        }
        if (this.result === TransactionModel.Results.unknown) {
            return `Transaction scheduled ${processedDate}`;
        }
        return `Transaction ${this.result} ${processedDate}`;
    }

    static getStatusMap(status) {
        return {
            isPending: status === 'pending',
            isWaitingCapture: status === 'waiting-capture',
        };
    }

    static getResultMap(result) {
        return {
            isApproved: result === 'approved',
            isUnknown: result === 'unknown',
        };
    }

    static getTypeMap(type) {
        return {
            isAuthorize: type === 'authorize',
            isCapture: type === 'capture',
            isSale: type === 'sale',
        };
    }

    getCapturePayload({amount}) {
        return {
            paymentInstrument: this.paymentInstrument.getPayload(),
            customerId: this.customerId,
            websiteId: this.websiteId,
            currency: this.currency,
            billingAddress: this.billingAddress.getPayload(),
            type: TransactionModel.Types.sale,
            amount,
        };
    }
}
